import { Divider, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';

import {
  DocumentMessageCard,
  type RocDocumentDownloadArgs
} from '@/components/roc/DocumentMessageCard';
import {
  documentEventSide,
  messageSide,
  type ReferralSide
} from '@/pages/CRM/ReferralOutbound/lib/callerIdentity';
import { type ReadState } from '@/pages/CRM/ReferralOutbound/ReferralManagement/lib/readReceipts';
import { type ReferralMessageModel } from '@/pages/CRM/ReferralOutbound/ReferralManagement/models/ReferralMessageModel';
import { MessageSystemEventTypeEnum } from '@/types/referral-outbound-center/models/Message';

interface ReferralMessageProps {
  model: ReferralMessageModel;
  /**
   * Which side of the referral is looking. The sender's thread view and a
   * recipient's are the same component, so "is this mine" is not a property
   * of the message on its own.
   */
  viewerSide: ReferralSide;
  /**
   * The referral's own sender facility. Only used to attribute a document
   * notice on the Dynamo read path.
   */
  referralSenderFacilityId: string;
  /** Label for the viewer's own side. */
  ownSideName: string;
  /** Label for the other side. */
  otherSideName: string;
  prevModel: ReferralMessageModel | null;
  readState?: ReadState | null;
  onOpenDocument?: (args: RocDocumentDownloadArgs) => Promise<void>;
}

interface DocumentSystemMessageProps {
  model: ReferralMessageModel;
  isMine: boolean;
  facilityLabel: string;
  onOpenDocument?: (args: RocDocumentDownloadArgs) => Promise<void>;
}

const DocumentSystemMessage: React.FC<DocumentSystemMessageProps> = ({
  model,
  isMine,
  facilityLabel,
  onOpenDocument
}) => {
  const docData = model.getDocumentAddedData();
  return (
    <Stack gap={1} alignItems={isMine ? 'flex-end' : 'flex-start'} width="100%">
      <Stack direction="row" gap={1} alignItems="center">
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 500,
            color: 'text.primary',
            lineHeight: '16px'
          }}
        >
          {facilityLabel}
        </Typography>
        <Typography
          sx={{
            fontSize: 10,
            color: 'text.secondary',
            lineHeight: '14px'
          }}
        >
          {model.formattedTime()}
        </Typography>
      </Stack>
      {docData ? (
        <DocumentMessageCard
          referralId={model.referral_id}
          documentId={docData.document_id}
          fileName={docData.file_name}
          fileSizeBytes={docData.file_size_bytes}
          isSender={isMine}
          onOpenDocument={onOpenDocument}
        />
      ) : (
        <Typography
          sx={{
            fontSize: 12,
            color: 'text.secondary',
            fontStyle: 'italic'
          }}
        >
          A document was shared but could not be displayed. Refresh, and contact
          Support if the problem persists.
        </Typography>
      )}
    </Stack>
  );
};

export const ReferralMessage: React.FC<ReferralMessageProps> = ({
  model,
  viewerSide,
  referralSenderFacilityId,
  ownSideName,
  otherSideName,
  prevModel,
  readState,
  onOpenDocument
}) => {
  const isNewDay = model.isNewDay(prevModel);
  const isDocumentEvent =
    model.message_type === 'system' &&
    model.system_event_type === MessageSystemEventTypeEnum.DocumentAdded;

  // A document notice is a system message, so it has no author participant
  // however the file got there. Its side comes off the upload instead.
  const authorSide = isDocumentEvent
    ? documentEventSide(
        {
          uploadedByParticipantId:
            model.getDocumentAddedData()?.uploaded_by_participant_id,
          messageSenderFacilityId: model.sender_facility_id
        },
        referralSenderFacilityId
      )
    : messageSide(model);

  const isMine = authorSide === viewerSide;

  return (
    <Stack gap={0.5} width="100%">
      {isNewDay && model.message_type === 'user' && (
        <Divider sx={{ fontSize: 10, color: 'text.secondary', my: 0.5 }}>
          {model.formattedDateTime().split(',').slice(0, 2).join(',')}
        </Divider>
      )}

      {isDocumentEvent ? (
        <DocumentSystemMessage
          model={model}
          isMine={isMine}
          facilityLabel={isMine ? ownSideName : otherSideName}
          onOpenDocument={onOpenDocument}
        />
      ) : model.message_type === 'system' ? (
        <SystemMessageRow>
          <Typography
            sx={{
              fontSize: 10,
              color: 'text.secondary',
              textAlign: 'center',
              lineHeight: '14px'
            }}
          >
            {model.formattedDateTime()}
          </Typography>
          <Typography
            sx={{
              fontSize: 12,
              color: 'text.primary',
              textAlign: 'center',
              lineHeight: '16px'
            }}
          >
            {model.content}
          </Typography>
        </SystemMessageRow>
      ) : isMine ? (
        <Stack gap={1} alignItems="flex-end" width="100%">
          <Stack alignItems="flex-end" gap={0}>
            <Stack direction="row" gap={1} alignItems="center">
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: 'text.primary',
                  lineHeight: '16px'
                }}
              >
                {model.sender_user_name || ownSideName}
              </Typography>
              <Typography
                sx={{
                  fontSize: 10,
                  color: 'text.secondary',
                  lineHeight: '14px'
                }}
              >
                {model.formattedTime()}
              </Typography>
            </Stack>
            {model.sender_user_title && (
              <Typography
                sx={{
                  fontSize: 12,
                  color: 'text.secondary',
                  lineHeight: '16px'
                }}
              >
                {model.sender_user_title}
              </Typography>
            )}
          </Stack>
          <SenderBubble>
            <Typography
              sx={{ fontSize: 14, color: 'common.white', lineHeight: '20px' }}
            >
              {model.content}
            </Typography>
          </SenderBubble>
          {readState && (
            <Typography
              variant="caption"
              color={readState.firstReadAt ? 'text.primary' : 'text.secondary'}
              sx={{ alignSelf: 'flex-end', mt: 0.25, fontSize: 10 }}
            >
              {readState.firstReadAt
                ? `Read ${dayjs(readState.firstReadAt).local().format('h:mm A')}`
                : 'Delivered'}
            </Typography>
          )}
        </Stack>
      ) : (
        <Stack gap={1} alignItems="flex-start" width="100%">
          <Stack alignItems="flex-start" gap={0}>
            <Stack direction="row" gap={1} alignItems="center">
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: 'text.primary',
                  lineHeight: '16px'
                }}
              >
                {model.sender_user_name || otherSideName}
              </Typography>
              <Typography
                sx={{
                  fontSize: 10,
                  color: 'text.secondary',
                  lineHeight: '14px'
                }}
              >
                {model.formattedTime()}
              </Typography>
            </Stack>
            {model.sender_user_title && (
              <Typography
                sx={{
                  fontSize: 12,
                  color: 'text.secondary',
                  lineHeight: '16px'
                }}
              >
                {model.sender_user_title}
              </Typography>
            )}
          </Stack>
          <RecipientBubble>
            <Typography
              sx={{ fontSize: 14, color: 'text.primary', lineHeight: '20px' }}
            >
              {model.content}
            </Typography>
          </RecipientBubble>
        </Stack>
      )}
    </Stack>
  );
};

const SystemMessageRow = styled(Stack)({
  width: '100%',
  alignItems: 'center',
  gap: 2,
  padding: '4px 0'
});

const SenderBubble = styled(Stack)(({ theme }) => ({
  background: theme.palette.primary.dark,
  borderRadius: 8,
  maxWidth: 438,
  padding: 16
}));

const RecipientBubble = styled(Stack)(({ theme }) => ({
  background: theme.palette.info.main,
  borderRadius: 8,
  maxWidth: 438,
  padding: 16
}));
